/* Product-scoped GOYOJO interactions. Direct checkout uses a separate Storefront
   API cart, never /cart/clear or an Ajax-cart replacement. */
(() => {
  'use strict';
  const init = () => document.querySelectorAll('[data-goyojo-product]').forEach(root => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const configNode = root.querySelector('[data-product-config]');
    let config;
    try { config = JSON.parse(configNode.textContent); } catch { return; }
    const t = (key, values = {}) => Object.entries(values).reduce((value, [name, replacement]) => value.split(`{${name}}`).join(String(replacement)), config.strings?.[key] || key);
    const marketCurrencies = { DE: 'EUR', AT: 'EUR', RO: 'RON', HR: 'EUR', HU: 'HUF', PT: 'EUR', CZ: 'CZK', PL: 'PLN' };
    const ajaxUrl = path => `${(config.rootUrl || '/').replace(/\/$/, '')}/${path}`;
    const q = selector => root.querySelector(selector);
    const qa = selector => [...root.querySelectorAll(selector)];
    // Same-product Shopify localization, shared with the shop's native country menu.
    const countryPicker = q('[data-gj-country-picker]');
    const countryToggle = q('[data-gj-country-toggle]');
    const countryMenu = q('[data-gj-country-menu]');
    const countryForms = qa('[data-gj-country-form]');
    const updateCountryReturns = () => countryForms.forEach(localizationForm => {
      const input = localizationForm.querySelector('input[name="return_to"]');
      const button = localizationForm.querySelector('[name="country_code"]');
      if (!input || !button) return;
      const destination = new URL(input.value, location.origin);
      destination.searchParams.set('country', button.value);
      const variant = q('input[name="id"]:checked')?.value || config.selectedId;
      if (variant) destination.searchParams.set('variant', variant);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) destination.searchParams.set('preview_theme_id', preview);
      destination.hash = location.hash;
      input.value = destination.pathname + destination.search + destination.hash;
    });
    const closeCountryMenu = (focus = false) => {
      if (!countryMenu || !countryToggle) return;
      countryMenu.hidden = true; countryToggle.setAttribute('aria-expanded', 'false');
      if (focus) countryToggle.focus({ preventScroll: true });
    };
    countryToggle?.addEventListener('click', () => {
      const open = countryMenu.hidden; countryMenu.hidden = !open;
      countryToggle.setAttribute('aria-expanded', String(open));
      if (open) { updateCountryReturns(); countryMenu.querySelector('[aria-checked="true"]')?.focus(); }
    });
    countryPicker?.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); closeCountryMenu(true); }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault(); countryMenu.hidden = false; countryToggle.setAttribute('aria-expanded', 'true');
      const buttons = [...countryMenu.querySelectorAll('[name="country_code"]')];
      const index = buttons.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowUp' ? -1 : 1) + buttons.length) % buttons.length;
      buttons[next]?.focus();
    });
    document.addEventListener('pointerdown', event => { if (countryPicker && !countryPicker.contains(event.target)) closeCountryMenu(); });
    matchMedia('(min-width:1068px) and (hover:hover) and (pointer:fine)').addEventListener('change', event => { if (!event.matches) closeCountryMenu(); });
    countryForms.forEach(localizationForm => localizationForm.addEventListener('submit', () => {
      updateCountryReturns();
      // Shopify may normalize the unprefixed return URL. Preserve the explicit
      // country/language choice across that navigation before any browser hint.
      try { sessionStorage.setItem('gj-initial-language', localizationForm.querySelector('[name="language_code"]')?.value || config.locale); } catch { /* Storage is optional. */ }
    }));
    window.addEventListener('hashchange', updateCountryReturns);
    updateCountryReturns();
    // The unprefixed product route is the shop's Romanian default. A first visit
    // without an explicit market/locale follows a supported browser language.
    const currentUrl = new URL(location.href);
    const explicitCountryValues = [...currentUrl.searchParams].filter(([key]) => key.toLowerCase() === 'country').map(([,value]) => value.toUpperCase());
    if (explicitCountryValues.length && explicitCountryValues.every(value => value === explicitCountryValues[0]) && marketCurrencies[explicitCountryValues[0]]) {
      try { sessionStorage.setItem('gj-initial-language', config.locale); } catch { /* Storage is optional. */ }
    }
    const explicitLocale = Object.values(config.languageRoots || {}).some(value => {
      const prefix = new URL(value || '/', location.origin).pathname.replace(/\/$/, '');
      return prefix && (location.pathname === prefix || location.pathname.startsWith(`${prefix}/`));
    });
    if (![...currentUrl.searchParams.keys()].some(key => key.toLowerCase() === 'country') && !explicitLocale) {
      const languageMarkets = { ro: 'RO', hr: 'HR', hu: 'HU', pt: 'PT', cs: 'CZ', de: 'DE', pl: 'PL' };
      const browserLanguage = (navigator.languages || [navigator.language]).find(value => languageMarkets[String(value).split('-')[0].toLowerCase()]);
      const baseLanguage = String(browserLanguage || 'de').split('-')[0].toLowerCase();
      const firstCountry = /^de-at$/i.test(browserLanguage || '') ? 'AT' : languageMarkets[baseLanguage];
      const firstLocale = baseLanguage === 'pt' ? 'pt-PT' : baseLanguage;
      try {
        if (!sessionStorage.getItem('gj-initial-language')) {
          sessionStorage.setItem('gj-initial-language', firstLocale);
          if (config.country !== firstCountry || config.locale !== firstLocale) {
            const button = countryForms.flatMap(localizationForm => [...localizationForm.querySelectorAll('[name="country_code"]')]).find(node => node.value === firstCountry);
            if (button) { button.form.requestSubmit(button); return; }
          }
        }
      } catch { /* An explicit country link remains usable when storage is disabled. */ }
    }
    const requestedCountries = [...new URL(location.href).searchParams].filter(([key]) => key.toLowerCase() === 'country').map(([,value]) => value.toUpperCase());
    if (requestedCountries.length && requestedCountries.every(country => country === requestedCountries[0]) && marketCurrencies[requestedCountries[0]] && requestedCountries[0] !== config.country) {
      const targetButton = countryForms.flatMap(localizationForm => [...localizationForm.querySelectorAll('[name="country_code"]')]).find(button => button.value === requestedCountries[0]);
      const attempt = `${location.pathname}:${requestedCountries[0]}`;
      try {
        if (targetButton && sessionStorage.getItem('gj-country-attempt') !== attempt) {
          sessionStorage.setItem('gj-country-attempt', attempt); targetButton.form.requestSubmit(targetButton); return;
        }
      } catch { /* A mismatched native context stays unable to checkout below. */ }
    } else { try { sessionStorage.removeItem('gj-country-attempt'); } catch { /* Storage may be disabled. */ } }
    const newsletter = q('[data-footer-newsletter]');
    if (newsletter) {
      const email = newsletter.querySelector('[data-footer-email]');
      const consent = newsletter.querySelector('[data-footer-consent]');
      const feedback = newsletter.querySelector('[data-footer-feedback]');
      const confirmEmail = () => {
        email.value = email.value.trim();
        if (!email.reportValidity() || !consent.reportValidity()) return;
        email.value = '';
        consent.checked = false;
        feedback.textContent = t('newsletterThanks');
        feedback.hidden = false;
      };
      newsletter.querySelector('[data-footer-confirm]').addEventListener('click', confirmEmail);
      email.addEventListener('keydown', event => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        confirmEmail();
      });
      newsletter.addEventListener('input', () => { feedback.hidden = true; });
    }
    // Illustrative image comparison. No timers, cart writes or external requests.
    qa('[data-gj-compare]').forEach(compare => {
      const stage = compare.querySelector('[data-gj-compare-stage]');
      const range = compare.querySelector('[data-gj-compare-range]');
      if (!stage || !range || compare.dataset.compareInitialized) return;
      compare.dataset.compareInitialized = 'true';
      const number = new Intl.NumberFormat(config.formatLocale, { maximumFractionDigits: 1 });
      const renderComparison = value => {
        const position = Math.max(0, Math.min(100, Number(value) || 0));
        range.value = position.toFixed(1);
        stage.style.setProperty('--gj-compare-position', `${range.value}%`);
        range.setAttribute('aria-valuetext', t('sliderValue', { visible: number.format(Number(range.value)), thermal: number.format(100 - Number(range.value)) }));
      };
      const moveComparison = clientX => {
        const bounds = stage.getBoundingClientRect();
        if (bounds.width) renderComparison((clientX - bounds.left) / bounds.width * 100);
      };
      let pointer = null;
      range.addEventListener('input', () => renderComparison(range.value));
      range.addEventListener('keydown', event => {
        const increments = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1, PageDown: -10, PageUp: 10 };
        if (event.key === 'Home' || event.key === 'End' || Object.hasOwn(increments, event.key)) {
          event.preventDefault();
          renderComparison(event.key === 'Home' ? 0 : event.key === 'End' ? 100 : Number(range.value) + increments[event.key]);
        }
      });
      stage.addEventListener('pointerdown', event => {
        if (pointer || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        const bounds = stage.getBoundingClientRect();
        const lineX = bounds.left + bounds.width * Number(range.value) / 100;
        const distanceX = Math.abs(event.clientX - lineX);
        const distanceY = Math.abs(event.clientY - bounds.top - bounds.height / 2);
        if (distanceX > 2 && (distanceX > 30 || distanceY > 30)) return;
        pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, kind: event.pointerType, horizontal: false };
        stage.setPointerCapture(event.pointerId);
        range.focus({ preventScroll: true });
      });
      stage.addEventListener('pointermove', event => {
        if (!pointer || pointer.id !== event.pointerId) return;
        const dx = Math.abs(event.clientX - pointer.x), dy = Math.abs(event.clientY - pointer.y);
        if (!pointer.horizontal && (pointer.kind === 'mouse' ? dx > 1 : dx > 6 && dx > dy)) pointer.horizontal = true;
        if (pointer.horizontal) moveComparison(event.clientX);
      });
      const endComparison = event => {
        if (!pointer || pointer.id !== event.pointerId) return;
        if (event.type === 'pointerup') {
          if (pointer.horizontal) moveComparison(event.clientX);
        }
        pointer = null;
        if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
      };
      stage.addEventListener('pointerup', endComparison);
      stage.addEventListener('pointercancel', endComparison);
      stage.addEventListener('lostpointercapture', () => { pointer = null; });
      renderComparison(range.value);
    });

    const form = q('[data-product-form]');
    const qty = q('[data-quantity]');
    const status = q('[data-commerce-status]');
    const purchaseBubble = q('[data-purchase-bubble]');
    const purchasePanel = q('[data-bubble-panel]');
    const purchaseToggle = q('[data-bubble-toggle]');
    const purchaseStatus = q('[data-bubble-status]');
    const bubbleVariant = q('[data-bubble-variant]');
    const bubbleQuantity = q('[data-bubble-quantity]');
    const mainQuantityNotice = q('[data-quantity-limit-main]');
    const bubbleQuantityNotice = q('[data-quantity-limit-bubble]');
    let bubbleOrderRequested = false, busy = false;
    const quantityNoticeTimers = new Map();
    const clearQuantityLimit = notice => {
      clearTimeout(quantityNoticeTimers.get(notice)); quantityNoticeTimers.delete(notice);
      notice.hidden = true; notice.textContent = '';
    };
    const showQuantityLimit = (source = qty) => {
      syncQuantity();
      if (busy) return;
      const notice = purchasePanel.contains(source) ? bubbleQuantityNotice : mainQuantityNotice;
      clearQuantityLimit(notice);
      notice.textContent = t('quantityLimit'); notice.hidden = false;
      quantityNoticeTimers.set(notice, setTimeout(() => clearQuantityLimit(notice), 3000));
    };
    const setPurchasePanel = (open, restoreFocus = true) => {
      const focusInside = purchasePanel.contains(document.activeElement);
      purchasePanel.hidden = !open;
      if (!open) clearQuantityLimit(bubbleQuantityNotice);
      purchaseToggle.setAttribute('aria-expanded', String(open));
      purchaseToggle.setAttribute('aria-label', open ? t('purchaseClose') : t('purchaseOpen'));
      if (open) {
        purchaseStatus.hidden = true; purchaseStatus.textContent = '';
        syncQuantity(); bubbleVariant.value = String(selected.id);
        purchasePanel.focus({ preventScroll: true });
      } else if (restoreFocus && focusInside) purchaseToggle.focus({ preventScroll: true });
    };
    const message = text => {
      status.hidden = false; status.textContent = text;
      if (bubbleOrderRequested) {
        setPurchasePanel(false);
        purchaseStatus.textContent = text; purchaseStatus.hidden = false;
      }
    };
    let selected = config.variants.find(v => String(v.id) === String(config.selectedId)) || config.variants[0];
    const pickVariant = (id, updateUrl = true) => {
      const found = config.variants.find(v => String(v.id) === String(id));
      if (!found) return;
      selected = found;
      qa('input[name="id"]').forEach(input => { input.checked = String(input.value) === String(id); });
      bubbleVariant.value = String(found.id);
      qa('[data-variant-label]').forEach(node => { node.textContent = found.title; });
      qa('[data-price]').forEach(node => { node.textContent = found.formattedPrice; });
      qa('[data-model-card]').forEach(card => {
        const model = config.variants.find(variant => String(variant.id) === card.dataset.modelCard);
        if (!model) return;
        const active = String(model.id) === String(found.id);
        card.classList.toggle('is-selected', active);
        card.querySelector('[data-model-price]').textContent = model.formattedPrice;
        card.querySelector('[data-model-selected]').hidden = !active;
        card.querySelector('[data-model-select]').setAttribute('aria-current', String(active));
      });
      qa('[data-model-pick]').forEach(button => {
        const active = String(button.dataset.modelPick) === String(found.id);
        button.setAttribute('aria-pressed', String(active));
        button.querySelector('[data-model-pick-label]').textContent = active ? t('modelSelected') : t('modelSelect');
      });
      qa('[data-model-column]').forEach(cell => cell.classList.toggle('is-selected', String(cell.dataset.modelColumn) === String(found.id)));
      const compared = Number(found.compareAtPrice) > Number(found.price);
      const comparison = q('[data-compare-price]');
      comparison.hidden = !compared;
      comparison.textContent = found.formattedCompareAtPrice;
      const badge = q('[data-sale-badge]');
      badge.hidden = !config.clearanceEnabled || !compared;
      qa('[data-clearance-hint]').forEach(node => { node.hidden = !config.clearanceEnabled || !compared; });
      badge.textContent = compared ? t('savings', { percent: Math.round((found.compareAtPrice - found.price) / found.compareAtPrice * 100) }) : '';
      if (updateUrl) { const url = new URL(location.href); url.searchParams.set('variant', id); history.replaceState({}, '', url); }
      updateCountryReturns();
      status.hidden = true;
      purchaseStatus.hidden = true; purchaseStatus.textContent = '';
    };
    qa('input[name="id"]').forEach(input => input.addEventListener('change', () => pickVariant(input.value)));
    bubbleVariant.addEventListener('change', () => pickVariant(bubbleVariant.value));
    const chooseComparedModel = id => {
      if (busy) return;
      pickVariant(id); syncQuantity(); setPurchasePanel(false, false);
    };
    qa('[data-model-pick]').forEach(button => {
      button.hidden = false;
      button.addEventListener('click', () => chooseComparedModel(button.dataset.modelPick));
    });
    q('[data-model-table]')?.addEventListener('click', event => {
      if (event.target.closest('[data-model-pick]')) return;
      const cell = event.target.closest('[data-model-column]');
      if (cell) chooseComparedModel(cell.dataset.modelColumn);
    });
    qa('[data-model-select]').forEach(link => link.addEventListener('click', event => {
      event.preventDefault();
      if (busy) return;
      pickVariant(link.dataset.modelSelect); syncQuantity(); setPurchasePanel(false, false);
      const destination = new URL(location.href); destination.hash = 'gj-order'; history.replaceState({}, '', destination);
      form.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      form.focus({ preventScroll: true });
    }));
    const quantityValue = () => 1;
    const syncQuantity = () => {
      [qty, bubbleQuantity].forEach(input => { input.value = '1'; input.min = '1'; input.max = '1'; input.readOnly = true; });
      qa('[data-quantity-minus],[data-bubble-quantity-minus]').forEach(button => { button.disabled = true; });
      qa('[data-quantity-plus],[data-bubble-quantity-plus]').forEach(button => { button.disabled = busy; });
    };
    qa('[data-quantity-plus],[data-bubble-quantity-plus]').forEach(button => button.addEventListener('click', () => showQuantityLimit(button)));
    [qty, bubbleQuantity].forEach(input => {
      const normalize = () => { const overLimit = Number(input.value) > 1; syncQuantity(); if (overLimit) showQuantityLimit(input); };
      input.addEventListener('input', normalize); input.addEventListener('change', normalize);
      input.addEventListener('keydown', event => {
        if (event.key === 'ArrowUp') { event.preventDefault(); showQuantityLimit(input); }
        else if (event.key === 'ArrowDown') { event.preventDefault(); syncQuantity(); }
      });
    });
    form.addEventListener('reset', () => queueMicrotask(syncQuantity));
    syncQuantity(); pickVariant(selected.id, false);
    purchaseToggle.addEventListener('click', () => setPurchasePanel(purchasePanel.hidden));
    q('[data-bubble-close]').addEventListener('click', () => setPurchasePanel(false));
    document.addEventListener('pointerdown', event => { if (!purchasePanel.hidden && !purchaseBubble.contains(event.target)) setPurchasePanel(false, false); });
    root.addEventListener('keydown', event => { if (event.key === 'Escape' && !purchasePanel.hidden) { event.preventDefault(); setPurchasePanel(false); } });

    const slides = qa('[data-gallery-image]');
    const galleryMain = q('.gj-gallery-main');
    const lightbox = q('[data-lightbox]');
    let imageIndex = 0, zoomed = false, returnFocus, swipeSuppressUntil = 0;
    const fitLocalizedLightbox = () => {
      const stage = q('[data-lightbox-stage]');
      const media = stage.querySelector('[data-lightbox-localized]');
      if (!media || !lightbox.open) return;
      const viewBox = media.querySelector('svg')?.viewBox.baseVal;
      const ratio = viewBox?.width / viewBox?.height || 1;
      const width = Math.min(stage.clientWidth, stage.clientHeight * ratio);
      media.style.width = `${width}px`; media.style.height = `${width / ratio}px`;
    };
    window.addEventListener('resize', fitLocalizedLightbox);
    const gallerySelect = index => {
      imageIndex = (index + slides.length) % slides.length;
      q('[data-gallery-track]').style.transform = `translateX(${-imageIndex * 100}%)`;
      qa('[data-gallery-thumb]').forEach((node, i) => node.setAttribute('aria-current', String(i === imageIndex)));
      qa('[data-gallery-dot]').forEach((node, i) => node.setAttribute('aria-current', String(i === imageIndex)));
      slides.forEach((node, i) => { node.tabIndex = i === imageIndex ? 0 : -1; node.setAttribute('aria-hidden', String(i !== imageIndex)); });
      if (lightbox.open) updateLightbox();
    };
    const setZoom = value => { zoomed = value; q('[data-lightbox-stage]').classList.toggle('is-zoomed', value); q('[data-lightbox-zoom]').textContent = value ? '−' : '+'; };
    const updateLightbox = () => {
      const source = slides[imageIndex].querySelector('img');
      const image = q('[data-lightbox-image]');
      const stage = q('[data-lightbox-stage]');
      stage.querySelector('[data-lightbox-localized]')?.remove();
      const localized = slides[imageIndex].querySelector('[data-localized-media]');
      image.hidden = !!localized;
      if (localized) {
        const copy = localized.cloneNode(true);
        copy.dataset.lightboxLocalized = '';
        copy.addEventListener('click', () => setZoom(!zoomed));
        stage.append(copy); requestAnimationFrame(fitLocalizedLightbox);
      }
      image.src = source.currentSrc || source.src;
      image.alt = localized?.getAttribute('aria-label') || source.alt || `GOYOJO ${imageIndex + 1}`;
      q('[data-lightbox-counter]').textContent = `${imageIndex + 1} / ${slides.length}`;
      qa('[data-lightbox-thumb]').forEach((node, i) => node.setAttribute('aria-current', String(i === imageIndex)));
      setZoom(false);
    };
    const openLightbox = () => { returnFocus = document.activeElement; updateLightbox(); lightbox.showModal(); document.documentElement.style.overflow = 'hidden'; };
    const closeLightbox = () => lightbox.close();
    slides.forEach((node, i) => node.addEventListener('click', () => { if (Date.now() < swipeSuppressUntil) return; gallerySelect(i); openLightbox(); }));
    qa('[data-gallery-thumb]').forEach(node => node.addEventListener('click', () => gallerySelect(Number(node.dataset.galleryThumb))));
    qa('[data-gallery-dot]').forEach(node => node.addEventListener('click', () => gallerySelect(Number(node.dataset.galleryDot))));
    q('[data-gallery-prev]').addEventListener('click', () => gallerySelect(imageIndex - 1));
    q('[data-gallery-next]').addEventListener('click', () => gallerySelect(imageIndex + 1));
    q('[data-gallery-zoom]').addEventListener('click', openLightbox);
    q('[data-lightbox-prev]').addEventListener('click', () => gallerySelect(imageIndex - 1));
    q('[data-lightbox-next]').addEventListener('click', () => gallerySelect(imageIndex + 1));
    qa('[data-lightbox-thumb]').forEach(node => node.addEventListener('click', () => gallerySelect(Number(node.dataset.lightboxThumb))));
    q('[data-lightbox-close]').addEventListener('click', closeLightbox);
    q('[data-lightbox-zoom]').addEventListener('click', () => setZoom(!zoomed));
    q('[data-lightbox-image]').addEventListener('click', () => setZoom(!zoomed));
    lightbox.addEventListener('close', () => { document.documentElement.style.overflow = ''; setZoom(false); returnFocus?.focus(); });
    const arrowKeys = event => { if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); gallerySelect(imageIndex + (event.key === 'ArrowRight' ? 1 : -1)); } };
    galleryMain.addEventListener('keydown', arrowKeys); lightbox.addEventListener('keydown', arrowKeys);
    let startX = null, startY = null;
    galleryMain.addEventListener('pointerdown', event => { if (event.pointerType !== 'mouse') { startX = event.clientX; startY = event.clientY; } });
    galleryMain.addEventListener('pointerup', event => { if (startX !== null) { const delta = event.clientX - startX; if (Math.abs(delta) > 45 && Math.abs(delta) > Math.abs(event.clientY - startY)) { swipeSuppressUntil = Date.now() + 500; gallerySelect(imageIndex + (delta < 0 ? 1 : -1)); } startX = null; } });
    galleryMain.addEventListener('pointercancel', () => { startX = null; });
    gallerySelect(0);

    const announcementItems = config.country !== 'PL' && config.clearanceEnabled ? [config.clearanceText, ...config.announcements.filter(Boolean)] : config.announcements.filter(Boolean);
    let announcementIndex = 0, announcementTimer;
    const announcement = q('[data-announcement-text]');
    const announcementMove = direction => { announcementIndex = (announcementIndex + direction + announcementItems.length) % announcementItems.length; announcement.textContent = announcementItems[announcementIndex] || ''; };
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const stopAnnouncement = () => clearInterval(announcementTimer);
    const startAnnouncement = () => { stopAnnouncement(); if (!reducedMotion.matches && announcementItems.length > 1 && !document.hidden) announcementTimer = setInterval(() => announcementMove(1), 5000); };
    q('[data-announcement-prev]').addEventListener('click', () => { announcementMove(-1); startAnnouncement(); });
    q('[data-announcement-next]').addEventListener('click', () => { announcementMove(1); startAnnouncement(); });
    const bar = q('.gj-announcement');
    bar.addEventListener('mouseenter', stopAnnouncement); bar.addEventListener('mouseleave', startAnnouncement);
    bar.addEventListener('focusin', stopAnnouncement); bar.addEventListener('focusout', startAnnouncement);
    document.addEventListener('visibilitychange', startAnnouncement); reducedMotion.addEventListener('change', startAnnouncement); startAnnouncement();
    const reviewVideo = q('[data-review-video]');
    if (reviewVideo instanceof HTMLVideoElement) {
      const videoSection = reviewVideo.closest('.gj-review-video');
      const videoToggle = q('[data-video-toggle]');
      const videoLabel = q('[data-video-control-label]');
      const videoStatus = q('[data-video-status]');
      let videoInView = false, videoLoaded = false, manuallyPaused = false, autoplayBlocked = false, motionOverride = false, playbackRequest = 0;
      const shouldPlayVideo = () => videoInView && !document.hidden && !manuallyPaused && !autoplayBlocked && (!reducedMotion.matches || motionOverride);
      const videoControls = (state, text = '') => {
        videoSection.dataset.videoState = state;
        videoLabel.textContent = state === 'playing' ? t('videoPause') : t('videoPlay');
        videoToggle.setAttribute('aria-pressed', String(state === 'playing'));
        videoStatus.textContent = text; videoStatus.hidden = !text;
      };
      const videoPlayback = () => {
        const request = ++playbackRequest;
        reviewVideo.autoplay = shouldPlayVideo();
        if (!reviewVideo.autoplay) { reviewVideo.pause(); videoControls('paused'); return; }
        if (!videoLoaded) {
          reviewVideo.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
          videoLoaded = true; reviewVideo.load();
        }
        videoControls('loading');
        reviewVideo.play().then(() => {
          if (!shouldPlayVideo()) reviewVideo.pause();
          else videoControls('playing');
        }).catch(() => {
          if (request !== playbackRequest || !shouldPlayVideo()) return;
          autoplayBlocked = true; reviewVideo.autoplay = false;
          videoControls('paused', t('videoTap'));
        });
      };
      reviewVideo.muted = true; reviewVideo.defaultMuted = true; reviewVideo.autoplay = false;
      videoToggle.hidden = false;
      videoToggle.addEventListener('click', () => {
        if (!reviewVideo.paused) { manuallyPaused = true; videoPlayback(); }
        else { if (reviewVideo.error) videoLoaded = false; manuallyPaused = false; autoplayBlocked = false; motionOverride = reducedMotion.matches; videoPlayback(); }
      });
      reviewVideo.addEventListener('playing', () => { if (shouldPlayVideo()) videoControls('playing'); else reviewVideo.pause(); });
      reviewVideo.addEventListener('pause', () => { if (videoSection.dataset.videoState !== 'paused') videoControls('paused'); });
      reviewVideo.addEventListener('error', () => {
        autoplayBlocked = true; reviewVideo.autoplay = false;
        videoControls('paused', t('videoError'));
      });
      if (typeof IntersectionObserver === 'function') {
        new IntersectionObserver(entries => {
          videoInView = entries[0].isIntersecting;
          if (!videoInView) motionOverride = false;
          videoPlayback();
        }, { threshold: .05 }).observe(videoSection);
      } else {
        const updateVideoVisibility = () => {
          const bounds = videoSection.getBoundingClientRect();
          const inView = bounds.height > 0 && Math.max(0, Math.min(bounds.bottom, innerHeight) - Math.max(bounds.top, 0)) / bounds.height >= .05;
          if (inView === videoInView) return;
          videoInView = inView; if (!inView) motionOverride = false; videoPlayback();
        };
        window.addEventListener('scroll', updateVideoVisibility, { passive: true });
        window.addEventListener('resize', updateVideoVisibility); updateVideoVisibility();
      }
      document.addEventListener('visibilitychange', () => { if (document.hidden) motionOverride = false; videoPlayback(); });
      reducedMotion.addEventListener('change', () => { motionOverride = false; videoPlayback(); });
    }
    // Content stays visible until a supported, optional reveal observer is ready.
    qa('.gj-feature-copy,.gj-feature-image,.gj-feature-cards article,.gj-diagram>h2,.gj-diagram>.gj-prose,.gj-diagram>img').forEach(node => node.setAttribute('data-gj-motion', ''));
    const motionTargets = qa('[data-reveal],[data-gj-motion],[data-marker]');
    let motionObserver;
    const showAllMotion = () => {
      motionObserver?.disconnect(); root.classList.remove('gj-motion-ready');
      motionTargets.forEach(node => node.classList.add('is-visible'));
    };
    if (!reducedMotion.matches && typeof IntersectionObserver === 'function') {
      try {
        motionObserver = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) { entry.target.classList.add('is-visible'); motionObserver.unobserve(entry.target); }
        }), { threshold: .08 });
        motionTargets.forEach(node => {
          const bounds = node.getBoundingClientRect();
          if (bounds.bottom > 0 && bounds.top < innerHeight) node.classList.add('is-visible');
          else motionObserver.observe(node);
        });
        root.classList.add('gj-motion-ready');
      } catch { showAllMotion(); }
    } else showAllMotion();
    reducedMotion.addEventListener('change', () => { if (reducedMotion.matches) showAllMotion(); });

    // Specification height transitions keep native details as the no-JavaScript fallback.
    qa('.gj-spec').forEach(details => {
      const summary = details.querySelector('summary'), content = details.querySelector('.gj-spec-content');
      let expanded = details.open, heightAnimation = null;
      summary.setAttribute('aria-expanded', String(expanded));
      const settle = () => {
        const previous = heightAnimation; heightAnimation = null; previous?.cancel();
        details.open = expanded; details.style.removeProperty('height'); details.style.removeProperty('overflow');
        summary.setAttribute('aria-expanded', String(expanded));
      };
      const toggleSpecification = () => {
        const startHeight = details.getBoundingClientRect().height;
        heightAnimation?.cancel(); heightAnimation = null;
        expanded = !expanded; summary.setAttribute('aria-expanded', String(expanded));
        if (reducedMotion.matches || typeof details.animate !== 'function') { settle(); return; }
        details.style.height = `${startHeight}px`; details.style.overflow = 'hidden'; details.open = true;
        const style = getComputedStyle(details);
        const borderHeight = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
        const endHeight = summary.getBoundingClientRect().height + borderHeight + (expanded ? content.getBoundingClientRect().height : 0);
        heightAnimation = details.animate([{ height: `${startHeight}px` }, { height: `${endHeight}px` }], { duration: 440, easing: 'cubic-bezier(.25,.8,.25,1)', fill: 'both' });
        const currentAnimation = heightAnimation;
        heightAnimation.onfinish = () => { if (heightAnimation === currentAnimation) settle(); };
      };
      summary.addEventListener('click', event => { event.preventDefault(); toggleSpecification(); });
      reducedMotion.addEventListener('change', () => { if (reducedMotion.matches) settle(); });
      window.addEventListener('resize', () => { if (heightAnimation) settle(); });
    });

    const menu = q('#gj-menu');
    q('[data-menu-toggle]').addEventListener('click', event => { menu.hidden = !menu.hidden; event.currentTarget.setAttribute('aria-expanded', String(!menu.hidden)); });
    menu.querySelectorAll('a').forEach(link => link.addEventListener('click', () => { menu.hidden = true; q('[data-menu-toggle]').setAttribute('aria-expanded', 'false'); }));

    const cartDialog = q('[data-cart-dialog]');
    const cartMessage = text => { q('[data-cart-status]').textContent = text; };
    const renderCart = async () => {
      cartMessage(t('cartLoading'));
      try {
        const response = await fetch(ajaxUrl('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('cart-read');
        const cart = await response.json();
        q('[data-cart-count]').textContent = cart.item_count;
        const wrapper = q('[data-cart-items]'); wrapper.replaceChildren();
        (cart.items || []).forEach(item => {
          const row = document.createElement('div'); row.className = 'gj-cart-item';
          if (item.image) { const image = document.createElement('img'); image.src = item.image; image.alt = ''; row.append(image); }
          const description = document.createElement('div'); const title = document.createElement('strong'); title.textContent = item.product_title;
          const detail = document.createElement('p'); detail.textContent = `${item.variant_title && item.variant_title !== 'Default Title' ? `${item.variant_title} · ` : ''}${t('cartQuantity', { quantity: item.quantity })}`;
          const price = document.createElement('span'); price.textContent = new Intl.NumberFormat(config.formatLocale, { style: 'currency', currency: cart.currency || config.currency }).format(item.final_line_price / 100);
          description.append(title, detail, price); row.append(description); wrapper.append(row);
        });
        cartMessage(cart.items.length ? '' : t('cartEmpty'));
      } catch { cartMessage(t('cartError')); }
    };
    q('[data-cart-open]').addEventListener('click', () => { cartDialog.showModal(); renderCart(); });
    qa('[data-cart-close]').forEach(node => node.addEventListener('click', () => cartDialog.close()));
    const ready = () => {
      const countries = [...new URL(location.href).searchParams].filter(([key]) => key.toLowerCase() === 'country');
      if (!Object.hasOwn(marketCurrencies, config.country) || countries.some(([, value]) => value.toUpperCase() !== config.country)) { message(t('unsupportedCountry')); return false; }
      if (config.releaseReady !== true) { message(t('previewNotReady')); return false; }
      if (!selected?.available) { message(t('variantUnavailable')); return false; }
      if (config.currency !== marketCurrencies[config.country]) { message(t('currencyMismatch')); return false; }
      syncQuantity(); return true;
    };
    const setBusy = value => {
      if (busy === value) return;
      busy = value;
      qa('button,input,select').filter(node => !node.matches('[data-bubble-toggle],[data-bubble-close]')).forEach(node => { if (value) { node.dataset.wasDisabled = String(node.disabled); node.disabled = true; } else { node.disabled = node.dataset.wasDisabled === 'true'; delete node.dataset.wasDisabled; } });
      qa('[data-checkout],[data-add-cart]').forEach(node => { if (value) node.setAttribute('aria-busy', 'true'); else node.removeAttribute('aria-busy'); });
      purchaseToggle.setAttribute('aria-busy', String(value));
    };
    // A browser Back may restore a frozen checkout-loading interface from bfcache.
    window.addEventListener('pageshow', event => {
      syncQuantity();
      clearQuantityLimit(mainQuantityNotice); clearQuantityLimit(bubbleQuantityNotice);
      if (!event.persisted || !busy) return;
      setBusy(false);
      syncQuantity();
      status.hidden = true;
      status.textContent = '';
      setPurchasePanel(false);
      purchaseStatus.hidden = true; purchaseStatus.textContent = ''; bubbleOrderRequested = false;
    });
    const checkout = async () => {
      if (busy || !ready()) return;
      const id = String(selected.id), count = quantityValue(), expectedGid = `gid://shopify/ProductVariant/${id}`;
      setBusy(true); message(t('checkoutLoading'));
      const controller = new AbortController(); const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch('/api/2026-10/graphql.json', { method: 'POST', credentials: 'same-origin', signal: controller.signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query: 'mutation GoyojoCheckout($input:CartInput!,$country:CountryCode!,$language:LanguageCode!) @inContext(country:$country,language:$language){cartCreate(input:$input){cart{checkoutUrl lines(first:10){nodes{quantity merchandise{... on ProductVariant{id}}}} cost{totalAmount{amount currencyCode}}} userErrors{field message code}}}', variables: { country: config.country, language: config.languageEnum, input: { lines: [{ merchandiseId: expectedGid, quantity: count }], buyerIdentity: { countryCode: config.country }, attributes: [{ key: 'expected_delivery_country', value: config.country }] } } }) });
        const result = await response.json(); const payload = result.data?.cartCreate; const cart = payload?.cart;
        if (!response.ok || result.errors?.length || payload?.userErrors?.length || !cart?.checkoutUrl || cart.lines?.nodes?.length !== 1 || cart.lines.nodes[0].quantity !== count || cart.lines.nodes[0].merchandise?.id !== expectedGid || cart.cost?.totalAmount?.currencyCode !== config.currency) throw new Error('unexpected-cart');
        const destination = new URL(cart.checkoutUrl);
        const hosts = new Set(['cjuuri.shop', 'x58tw0-qu.myshopify.com']);
        const pathAllowed = /^\/checkouts\/[a-z0-9_-]+(?:\/|$)/i.test(destination.pathname) || /^\/(?:(?:de|ro|hr|hu|pt|cs|pl)\/)?cart\/c\/[a-z0-9_-]+\/?$/i.test(destination.pathname) || destination.pathname === '/checkout';
        if (destination.protocol !== 'https:' || destination.username || destination.password || !hosts.has(destination.hostname) || !pathAllowed) throw new Error('unexpected-checkout');
        destination.searchParams.set('locale', config.locale); location.assign(destination.href);
      } catch { message(t('checkoutError')); setBusy(false); }
      finally { clearTimeout(timeout); }
    };
    form.addEventListener('submit', event => { event.preventDefault(); bubbleOrderRequested = false; purchaseStatus.hidden = true; setPurchasePanel(false); checkout(); });
    qa('[data-checkout]').filter(button => button.type !== 'submit').forEach(button => button.addEventListener('click', () => {
      bubbleOrderRequested = button.hasAttribute('data-bubble-checkout');
      purchaseStatus.hidden = true; setPurchasePanel(false); checkout();
    }));
    q('[data-add-cart]').addEventListener('click', async () => {
      if (busy || !ready()) return;
      bubbleOrderRequested = false; purchaseStatus.hidden = true;
      const id = String(selected.id), count = quantityValue();
      setBusy(true); message(t('cartAdding'));
      try {
        const cartResponse = await fetch(ajaxUrl('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('cart-read');
        const existingCart = await cartResponse.json();
        if (!Array.isArray(existingCart.items)) throw new Error('cart-read');
        const targetVariants = new Set(config.variants.map(variant => String(variant.id)));
        const existingQuantity = existingCart.items.reduce((total, item) => targetVariants.has(String(item.variant_id ?? item.id)) ? total + Math.max(0, Number(item.quantity) || 0) : total, 0);
        if (existingQuantity >= 1) {
          status.hidden = true; status.textContent = '';
          setBusy(false); showQuantityLimit(qty); return;
        }
        const response = await fetch(ajaxUrl('cart/add.js'), { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: count }] }) });
        if (!response.ok) throw new Error('cart-add');
        await response.json(); message(t('cartAdded')); cartDialog.showModal(); await renderCart();
      } catch { message(t('cartAddError')); }
      finally { setBusy(false); }
    });
  });
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState !== 'loading') init();
})();
